import { AxiosInstance } from 'axios';
import { PaginatedResponse } from '@/types/common.ts';

function validatePage<T>(data: PaginatedResponse<T>, page: number, pageSize?: number) {
  const previousValid =
    page === 1
      ? data.previous === null
      : data.previous?.page === page - 1 && data.previous.page_size === pageSize;
  const nextValid =
    data.next === null ||
    (data.next?.page === page + 1 &&
      Number.isInteger(data.next.page_size) &&
      data.next.page_size > 0 &&
      (pageSize === undefined || data.next.page_size === pageSize));
  if (!previousValid || !nextValid) {
    throw new Error('Invalid data source pagination.');
  }
}

export async function fetchDatasetInventory<T extends { id: string }>(
  apiClient: AxiosInstance,
  url: string,
  signal: AbortSignal,
): Promise<PaginatedResponse<T>> {
  const response = await apiClient.get<PaginatedResponse<T>>(url, { signal });
  let inventory: PaginatedResponse<T> = response.data;
  validatePage(inventory, 1);
  const results = [...inventory.results];
  while (inventory.next) {
    const next = inventory.next;
    const response = await apiClient.get<PaginatedResponse<T>>(url, { signal, params: next });
    validatePage(response.data, next.page, next.page_size);
    if (response.data.count !== inventory.count) {
      throw new Error('Data sources changed during inspection. Please refresh.');
    }
    results.push(...response.data.results);
    inventory = {
      ...inventory,
      results,
      next: response.data.next,
    };
  }
  if (
    inventory.results.length !== inventory.count ||
    new Set(inventory.results.map((item) => item.id)).size !== inventory.count
  ) {
    throw new Error('Data sources changed during inspection. Please refresh.');
  }
  return inventory;
}
